import React, { useState, useEffect } from 'react';
import { deliveryAreaApi, Province } from '../services/deliveryAreaApi';
import { Search, RefreshCw, Save, CheckCircle, XCircle } from 'lucide-react';

interface CustomLocationsManagementTabProps {
  provinces: Province[];
  selectedProvince: Province | null;
}

interface CustomLocation {
  id: string;
  locationName: string;
  locationType: 'building' | 'street' | 'complex' | 'landmark';
  deliveryFee: number;
  estimatedDuration: string;
  isActive: boolean;
  neighborhoodId: string;
  province: string;
  district: string;
  neighborhood: string;
}

interface LocationEdit {
  neighborhoodId: string;
  customLocationId: string;
  deliveryFee: number;
  estimatedDuration: string;
  isActive: boolean;
}

export default function CustomLocationsManagementTab({ 
  provinces, 
  selectedProvince 
}: CustomLocationsManagementTabProps) {
  const [customLocations, setCustomLocations] = useState<CustomLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
  const [editedLocations, setEditedLocations] = useState<Map<string, LocationEdit>>(new Map());
  
  // Filters
  const [filters, setFilters] = useState({
    province: selectedProvince?.name || '',
    district: '',
    neighborhood: '',
    locationType: '',
    minFee: '',
    maxFee: '',
    isActive: 'all' as 'all' | 'true' | 'false',
  });

  // District list for dropdown
  const [districts, setDistricts] = useState<string[]>([]);

  useEffect(() => {
    if (selectedProvince) {
      setFilters(prev => ({ ...prev, province: selectedProvince.name }));
    }
  }, [selectedProvince]);

  useEffect(() => {
    // Update district list when province changes
    if (filters.province) {
      const province = provinces.find(p => p.name === filters.province);
      if (province) {
        const districtNames = province.districts.map((d: any) => d.name);
        setDistricts(districtNames);
      }
    }
  }, [filters.province, provinces]);

  const loadCustomLocations = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (filters.province) params.province = filters.province;
      if (filters.district) params.district = filters.district;
      if (filters.neighborhood) params.neighborhood = filters.neighborhood;
      if (filters.locationType) params.locationType = filters.locationType;
      if (filters.minFee) params.minFee = Number(filters.minFee);
      if (filters.maxFee) params.maxFee = Number(filters.maxFee);
      if (filters.isActive !== 'all') params.isActive = filters.isActive === 'true';

      const data = await deliveryAreaApi.getAllCustomLocations(params);
      setCustomLocations(data.customLocations);
      setEditedLocations(new Map());
      setSelectedLocations(new Set());
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedLocations.size === customLocations.length) {
      setSelectedLocations(new Set());
    } else {
      setSelectedLocations(new Set(customLocations.map(l => l.id)));
    }
  };

  const handleSelectLocation = (id: string) => {
    const newSelected = new Set(selectedLocations);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedLocations(newSelected);
  };

  const handleEdit = (location: CustomLocation, field: keyof LocationEdit, value: any) => {
    const currentEdit = editedLocations.get(location.id) || {
      neighborhoodId: location.neighborhoodId,
      customLocationId: location.id,
      deliveryFee: location.deliveryFee,
      estimatedDuration: location.estimatedDuration,
      isActive: location.isActive,
    };

    const newEdit = { ...currentEdit, [field]: value };
    const newEdited = new Map(editedLocations);
    newEdited.set(location.id, newEdit);
    setEditedLocations(newEdited);
  };

  const handleSave = async () => {
    if (editedLocations.size === 0) {
      alert('🔔 Değişiklik yok');
      return;
    }

    if (!confirm(`${editedLocations.size} özel konum güncellenecek. Onaylıyor musunuz?`)) {
      return;
    }

    try {
      setSaving(true);
      
      const updates = Array.from(editedLocations.values());
      const result = await deliveryAreaApi.bulkUpdateCustomLocations(updates);
      
      alert(`✅ ${result.updated} özel konum güncellendi!`);
      await loadCustomLocations();
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getLocationIcon = (type: string) => {
    switch (type) {
      case 'building': return '🏢';
      case 'street': return '🛣️';
      case 'complex': return '🏘️';
      case 'landmark': return '📍';
      default: return '📍';
    }
  };

  const getLocationTypeLabel = (type: string) => {
    switch (type) {
      case 'building': return 'Bina';
      case 'street': return 'Sokak/Cadde';
      case 'complex': return 'Site/Kompleks';
      case 'landmark': return 'İşaret Noktası';
      default: return type;
    }
  };

  const hasChanges = editedLocations.size > 0;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Filtreler</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              İl
            </label>
            <select
              value={filters.province}
              onChange={(e) => setFilters({ ...filters, province: e.target.value, district: '', neighborhood: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tümü</option>
              {provinces.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              İlçe
            </label>
            <select
              value={filters.district}
              onChange={(e) => setFilters({ ...filters, district: e.target.value, neighborhood: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!filters.province}
            >
              <option value="">Tümü</option>
              {districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mahalle
            </label>
            <input
              type="text"
              value={filters.neighborhood}
              onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value })}
              placeholder="Mahalle adı"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Konum Tipi
            </label>
            <select
              value={filters.locationType}
              onChange={(e) => setFilters({ ...filters, locationType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tümü</option>
              <option value="building">🏢 Bina</option>
              <option value="street">🛣️ Sokak/Cadde</option>
              <option value="complex">🏘️ Site/Kompleks</option>
              <option value="landmark">📍 İşaret Noktası</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Ücret (₺)
            </label>
            <input
              type="number"
              value={filters.minFee}
              onChange={(e) => setFilters({ ...filters, minFee: e.target.value })}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Ücret (₺)
            </label>
            <input
              type="number"
              value={filters.maxFee}
              onChange={(e) => setFilters({ ...filters, maxFee: e.target.value })}
              placeholder="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durum
            </label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tümü</option>
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={loadCustomLocations}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Search size={18} />
            {loading ? 'Aranıyor...' : 'Ara'}
          </button>
          
          <button
            onClick={() => {
              setFilters({
                province: selectedProvince?.name || '',
                district: '',
                neighborhood: '',
                locationType: '',
                minFee: '',
                maxFee: '',
                isActive: 'all',
              });
              setCustomLocations([]);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            <RefreshCw size={18} />
            Temizle
          </button>
        </div>
      </div>

      {/* Results */}
      {customLocations.length > 0 && (
        <>
          <div className="bg-white p-4 rounded-lg shadow flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <strong>{customLocations.length}</strong> özel konum bulundu
              {selectedLocations.size > 0 && (
                <span className="ml-2 text-blue-600">
                  • <strong>{selectedLocations.size}</strong> seçili
                </span>
              )}
              {hasChanges && (
                <span className="ml-2 text-orange-600">
                  • <strong>{editedLocations.size}</strong> değişmiş
                </span>
              )}
            </div>

            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save size={18} />
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedLocations.size === customLocations.length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Konum Adı
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tip
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İl / İlçe / Mahalle
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teslimat Ücreti (₺)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Süre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customLocations.map((location) => {
                    const isEdited = editedLocations.has(location.id);
                    const editData = editedLocations.get(location.id);
                    const displayFee = editData?.deliveryFee ?? location.deliveryFee;
                    const displayDuration = editData?.estimatedDuration ?? location.estimatedDuration;
                    const displayActive = editData?.isActive ?? location.isActive;

                    return (
                      <tr 
                        key={location.id} 
                        className={`hover:bg-gray-50 ${isEdited ? 'bg-yellow-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedLocations.has(location.id)}
                            onChange={() => handleSelectLocation(location.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            <span>{getLocationIcon(location.locationType)}</span>
                            <span>{location.locationName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {getLocationTypeLabel(location.locationType)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="text-xs">
                            <div>{location.province} / {location.district}</div>
                            <div className="text-gray-500">{location.neighborhood}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={displayFee}
                            onChange={(e) => handleEdit(location, 'deliveryFee', Number(e.target.value))}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            min="0"
                            step="5"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={displayDuration}
                            onChange={(e) => handleEdit(location, 'estimatedDuration', e.target.value)}
                            className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            placeholder="2-4 saat"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleEdit(location, 'isActive', !displayActive)}
                            className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-medium transition-colors ${
                              displayActive
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {displayActive ? (
                              <>
                                <CheckCircle size={14} />
                                Aktif
                              </>
                            ) : (
                              <>
                                <XCircle size={14} />
                                Pasif
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && customLocations.length === 0 && (
        <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
          Filtre uygulayarak özel konum arayın
        </div>
      )}
    </div>
  );
}
